import moment from "moment"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"
import Icon from "react-native-vector-icons/AntDesign"

import Base from "../../base"
import { AppButton, TextfieldModal } from "../common"
import Gap from "../common/Gap"
import { subscriptionApi } from "../subscriptions/api"
import useModalPrompt from "../subscriptions/hooks/useModalPrompt"

export default function WebReceiptList({ navigation, route }) {
  const [isLoading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [emailError, setEmailError] = useState(null)
  const [email, setEmail] = useState("")
  const transactions = useMemo(() => route?.params?.transactions ?? [], [route])
  const [selectedTransactions, setSelectedTransactions] = useState([])
  const [warningShown, setWarningShown] = useState(false)

  const [WarningModal, { show: showWarningModal }] = useModalPrompt(
    "Please Note",
    "Other customer's debit/credit cards may share the same last four digits as your own.\n\nAs a result your search may display more transactions than just yours.\n\nPlease ensure you only select your own transactions in the list.\n\nIf you require a receipt for transactions beyond 12 hours speak to a member of our team.",
    {
      successText: "OK. GOT IT",
    },
  )

  const [SubmissionErrorModal, { show: showSubmissionErrorModal }] =
    useModalPrompt(
      "Hold Up",
      "We were unable to send your receipt at this time. Please try again later",
      {
        successText: "OK",
      },
    )

  const [SubmissionSuccessModal, { show: showSubmissionSuccessModal }] =
    useModalPrompt(
      "Receipt Sent",
      `Your receipt has been sent to ${email}, thanks for drinking at Vagabond!`,
      {
        successText: "OK",
        onSuccess: () => {
          navigation?.goBack()
        },
      },
    )

  useEffect(() => {
    if (!warningShown) {
      showWarningModal()
      setWarningShown(true)
    }
  }, [showWarningModal, setWarningShown, warningShown])

  const getReceiptPressed = useCallback(() => {
    setModalVisible(true)
  }, [setModalVisible])

  const onEmailTextChange = useCallback(
    text => {
      setEmailError(null)
      setEmail(text)
    },
    [setEmail, setEmailError],
  )

  const sendReceiptPressed = useCallback(async () => {
    if (!Base.functions.validateEmail(email)) {
      setEmailError("Email not valid, check and try again")
      return
    }
    closeModal()
    setLoading(true)
    const response = await subscriptionApi.sendVATEmail({
      email,
      transactions: transactions.filter(x =>
        selectedTransactions.includes(x.id),
      ),
      venue: route?.params?.venue,
    })
    setLoading(false)
    if (!response?.success) {
      showSubmissionErrorModal()
    } else {
      showSubmissionSuccessModal()
    }
  }, [
    setEmailError,
    closeModal,
    email,
    setLoading,
    transactions,
    route,
    selectedTransactions,
    showSubmissionErrorModal,
    showSubmissionSuccessModal,
  ])

  const closeModal = useCallback(() => {
    setEmailError(null)
    setModalVisible(false)
  }, [setEmailError, setModalVisible])

  const onTransactionPressed = useCallback(
    transaction => {
      if (isLoading) return
      var _newTransactions = [...selectedTransactions]
      if (_newTransactions.includes(transaction.id)) {
        _newTransactions.splice(_newTransactions.indexOf(transaction.id), 1)
      } else {
        _newTransactions.push(transaction.id)
      }
      setSelectedTransactions(_newTransactions)
    },
    [selectedTransactions, setSelectedTransactions, isLoading],
  )

  const transactionTitle = useCallback(transaction => {
    const date = moment(transaction.created_at).format("dddd hh:mm a")
    const amount = Base.functions.formatPrice(
      transaction?.amount_money?.amount * 0.01,
    )
    const card =
      Base.functions.capitalise(
        transaction?.card_details?.card?.card_brand ?? "Unknown Card",
      ) +
      " " +
      (transaction?.card_details?.card?.last_4 ?? "XXXX")
    return date + " • " + amount + " • " + card
  }, [])

  const selectAllPressed = useCallback(() => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([])
    } else {
      setSelectedTransactions(transactions.map(x => x.id))
    }
  }, [transactions, setSelectedTransactions, selectedTransactions])

  const allSelected = useMemo(() => {
    return selectedTransactions.length === transactions.length
  }, [transactions, selectedTransactions])

  return (
    <View style={styles.main}>
      <ScrollView
        style={styles.scrollview}
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <View style={styles.controls}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              activeOpacity={0.8}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Icon name={"arrowleft"} color={Base.colours.appRed} size={28} />
            </TouchableOpacity>
            <View style={{ flex: 1 }} />
          </View>
          <Text style={styles.title}>NEED A RECEIPT?</Text>
          <Text style={styles.body}>
            Select transactions from the list below, then enter your email
            address to get a VAT receipt.
          </Text>
        </View>
        <View style={styles.card}>
          {(transactions ?? []).map((transaction, index) => {
            return (
              <TouchableOpacity
                style={[
                  styles.row,
                  {
                    backgroundColor: selectedTransactions.includes(
                      transaction.id,
                    )
                      ? Base.colours.appVeryLightGrey
                      : Base.colours.appWhite,
                  },
                ]}
                key={index}
                onPress={() => onTransactionPressed(transaction)}
                activeOpacity={0.9}
              >
                <View style={styles.rowInfo}>
                  <Text style={styles.rowTitle}>
                    {transactionTitle(transaction)}
                  </Text>
                  <Text style={styles.rowSubtitle}>{transaction?.note}</Text>
                </View>
                <Icon
                  name="checkcircle"
                  color={
                    selectedTransactions.includes(transaction.id)
                      ? Base.colours.appGreen
                      : Base.colours.appVeryLightGrey
                  }
                  size={25}
                />
              </TouchableOpacity>
            )
          })}
          <Gap size={120} />
        </View>
      </ScrollView>
      <View style={styles.buttonContainer}>
        <TouchableOpacity
          style={styles.selectAllButton}
          onPress={selectAllPressed}
        >
          <Text style={styles.selectAllTitle}>
            {allSelected ? "Select None" : "Select All"}
          </Text>
        </TouchableOpacity>
        <AppButton
          isLoading={isLoading}
          style={styles.emailButton}
          title={"GET VAT RECEIPT"}
          onPress={getReceiptPressed}
          titleStyle={{ color: Base.colours.appWhite }}
          disabled={selectedTransactions.length === 0}
        />
      </View>
      <TextfieldModal
        title={"Email Receipt"}
        description={"Enter your email address to receive your VAT receipt."}
        placeholder={"Email Address"}
        disclaimer={
          "Don't worry, we're not collecting your data or going to email you anything other than your receipt!"
        }
        successText={"SEND"}
        cancelText={"CANCEL"}
        visible={modalVisible}
        error={emailError}
        text={email}
        onChangeText={onEmailTextChange}
        onSuccess={sendReceiptPressed}
        onCancel={closeModal}
        onDismiss={closeModal}
      />
      <SubmissionErrorModal />
      <SubmissionSuccessModal />
      <WarningModal />
    </View>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
    backgroundColor: Base.colours.appBlack,
  },

  scrollview: {
    height: Base.constants.window.height,
  },

  container: {
    flexGrow: 1,
  },

  card: {
    backgroundColor: Base.colours.appWhite,
    borderTopLeftRadius: 40,
    borderTopRightRadius: 40,
    marginTop: -40,
    paddingHorizontal: 26,
    paddingVertical: 20,
    alignItems: "center",
    flex: 1,
  },

  header: {
    width: "100%",
    padding: 33,
    marginBottom: 30,
    alignItems: "center",
  },

  controls: {
    width: "100%",
    flexDirection: "row",
  },

  title: {
    fontFamily: Base.fonts.appTitle,
    color: Base.colours.appWhite,
    fontSize: 60,
    marginVertical: 18,
  },

  body: {
    fontFamily: Base.fonts.appRegular,
    fontSize: 16,
    color: Base.colours.appWhite,
    lineHeight: 23,
  },

  bodyBold: {
    fontFamily: Base.fonts.appBold,
  },

  buttonContainer: {
    position: "absolute",
    width: "100%",
    left: 0,
    bottom: 0,
    backgroundColor: Base.colours.appWhite,
    paddingBottom: 24,
    alignItems: "center",
  },

  emailButton: {
    backgroundColor: Base.colours.appRed,
  },

  row: {
    flexDirection: "row",
    paddingVertical: 10,
    marginVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 20,
    width: "100%",
    alignItems: "center",
  },

  rowInfo: {
    flex: 1,
    marginRight: 8,
  },

  rowTitle: {
    fontFamily: Base.fonts.appRegular,
    fontSize: 14,
    color: Base.colours.appBrown,
    marginBottom: 6,
  },

  rowSubtitle: {
    fontFamily: Base.fonts.appBold,
    fontSize: 17,
    color: Base.colours.appBrown,
  },

  selectAllButton: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    alignContent: "center",
  },

  selectAllTitle: {
    fontFamily: Base.fonts.appBold,
    fontSize: 16,
    color: Base.colours.appRed,
    marginHorizontal: 3,
    lineHeight: 23,
    textAlign: "center",
  },
})
